import { useState, useRef, useCallback, useEffect } from "react";
import { buildRealtimeFieldDetails } from "./realtimeFieldDiffs";

const DEFAULT_HIGHLIGHT_MS = 5000;

/**
 * Manages temporary field-highlight state driven by realtime SSE deltas.
 */
export function useRealtimeFieldHighlights(durationMs = DEFAULT_HIGHLIGHT_MS) {
  const timersRef = useRef(new Map());
  const [highlightsById, setHighlightsById] = useState({});
  const [highlightDetailsById, setHighlightDetailsById] = useState({});

  const clearHighlightForRecord = useCallback((recordId) => {
    setHighlightsById((prev) => {
      if (!prev[recordId]) return prev;
      const next = { ...prev };
      delete next[recordId];
      return next;
    });
    setHighlightDetailsById((prev) => {
      if (!prev[recordId]) return prev;
      const next = { ...prev };
      delete next[recordId];
      return next;
    });
    const timer = timersRef.current.get(recordId);
    if (timer) clearTimeout(timer);
    timersRef.current.delete(recordId);
  }, []);

  const scheduleHighlight = useCallback(
    (recordId, changedFields, previousRecord, nextRecord, rawChanges) => {
      if (!recordId || !changedFields?.length) return;

      const highlightDetails = buildRealtimeFieldDetails(
        previousRecord,
        nextRecord,
        rawChanges
      );

      setHighlightsById((prev) => ({
        ...prev,
        [recordId]: changedFields,
      }));
      setHighlightDetailsById((prev) => ({
        ...prev,
        [recordId]: highlightDetails,
      }));

      const existingTimer = timersRef.current.get(recordId);
      if (existingTimer) clearTimeout(existingTimer);

      const timer = setTimeout(() => {
        clearHighlightForRecord(recordId);
      }, durationMs);

      timersRef.current.set(recordId, timer);
    },
    [clearHighlightForRecord, durationMs]
  );

  const clearAllHighlights = useCallback(() => {
    timersRef.current.forEach((timer) => clearTimeout(timer));
    timersRef.current.clear();
    setHighlightsById({});
    setHighlightDetailsById({});
  }, []);

  useEffect(
    () => () => {
      timersRef.current.forEach((timer) => clearTimeout(timer));
      timersRef.current.clear();
    },
    []
  );

  return {
    highlightsById,
    highlightDetailsById,
    scheduleHighlight,
    clearAllHighlights,
    timersRef,
  };
}
